import type { CmsPage } from './apiTypes';

export type CmsAppLanguage = 'en' | 'ar';

export function resolveCmsPageLocalizedHtml(page: CmsPage | null | undefined, language: CmsAppLanguage): string {
  if (!page) {
    return '';
  }
  const html =
    language === 'ar' ? (page.a_content ?? page.content ?? '') : (page.content ?? page.a_content ?? '');
  return typeof html === 'string' ? html.trim() : '';
}

/**
 * Localized CMS page title. Look up pages by English `title` / `meta_title`;
 * display Arabic title fields when language is `ar` (never fall back to English —
 * callers use i18n when this returns empty).
 */
export function resolveCmsPageLocalizedTitle(page: CmsPage | null | undefined, language: CmsAppLanguage): string {
  if (!page) {
    return '';
  }
  const page_record = page as CmsPage & Record<string, unknown>;
  const en_title = pick_non_empty_string(page.title) ?? pick_non_empty_string(page_record.name) ?? '';
  const ar_title =
    pick_non_empty_string(page.a_title) ??
    pick_non_empty_string(page_record.arabic_title) ??
    pick_non_empty_string(page_record.title_ar) ??
    pick_non_empty_string(page_record.ar_title) ??
    '';
  if (language === 'ar') {
    return ar_title;
  }
  return en_title || ar_title;
}

function pick_non_empty_string(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Find a CMS page by English `title` or `meta_title` only (never match `a_title`). */
export function findCmsPageByTitleOrMetaTitle(
  pages: CmsPage[] | null | undefined,
  title: string,
  meta_title: string,
): CmsPage | null {
  if (!Array.isArray(pages)) {
    return null;
  }
  const title_normalized = title.trim().toLowerCase();
  const meta_normalized = meta_title.trim().toLowerCase();
  return (
    pages.find(page => {
      const page_title = String(page?.title ?? '').trim().toLowerCase();
      const page_meta = String(page?.meta_title ?? '').trim().toLowerCase();
      return page_title === title_normalized || page_meta === meta_normalized;
    }) ?? null
  );
}

export function findCmsPageByMetaTitle(pages: CmsPage[] | null | undefined, meta_title: string): CmsPage | null {
  if (!Array.isArray(pages)) {
    return null;
  }
  const meta_normalized = meta_title.trim().toLowerCase();
  return pages.find(page => String(page?.meta_title ?? '').trim().toLowerCase() === meta_normalized) ?? null;
}

export function isCmsNewsPage(page: CmsPage): boolean {
  const type = String(page.type ?? '').toLowerCase();
  const slug = String(page.slug ?? '').toLowerCase();
  const meta = String(page.meta_title ?? '').toLowerCase();
  const title = String(page.title ?? '').toLowerCase();
  const category = String(page.category ?? '').toLowerCase();
  return (
    type === 'news' ||
    meta === 'news' ||
    slug === 'news' ||
    slug.startsWith('news') ||
    slug.endsWith('-news') ||
    category === 'news' ||
    title.includes('news')
  );
}

export function filterNewsPages(pages: CmsPage[] | null | undefined): CmsPage[] {
  if (!Array.isArray(pages)) {
    return [];
  }
  const news = pages.filter(isCmsNewsPage);
  return news.length > 0 ? news : pages;
}
